import React, { useEffect, useMemo } from "react";
import { Form, Input, Select, Row, Col, Spin } from "antd";
import {
  CompassModal,
  ModalContent,
  ModalFooter,
  SelectableWithLogo,
} from "../../components/common/Modal";
import { CompassParagraph } from "../../components/common/styledComponents";
import { usePost } from "../../hooks/usePost";
import { useToast } from "../../store/ToastProvider";
import { ManagerItem } from "../../types/CommonDataRequestTypes";
import { greyText } from "../../styles";
import { MESSAGES } from "../../messages";
import linkIcon from "../../assets/svg/link.svg";

// --- Types ---
interface CreateMagicLinkRequest {
  email: string;
  manager_id: string;
}

interface CreateMagicLinkResponse {
  magic_link: string;
  expires_at: string;
}

interface MagicLinkFormValues {
  email: string;
  manager_id: string;
}

// --- Form Field Configuration ---
enum FormFieldName {
  EMAIL = "email",
  MANAGER = "manager_id",
}

interface FormFieldConfig {
  name: FormFieldName;
  label: string;
  placeholder: string;
  required: boolean;
  requiredMessage: string;
}

const FORM_FIELDS: Record<FormFieldName, FormFieldConfig> = {
  [FormFieldName.EMAIL]: {
    name: FormFieldName.EMAIL,
    label: MESSAGES.MAGIC_LINK.FORM.EMAIL_LABEL,
    placeholder: MESSAGES.MAGIC_LINK.FORM.EMAIL_PLACEHOLDER,
    required: true,
    requiredMessage: MESSAGES.MAGIC_LINK.FORM.EMAIL_REQUIRED,
  },
  [FormFieldName.MANAGER]: {
    name: FormFieldName.MANAGER,
    label: MESSAGES.MAGIC_LINK.FORM.MANAGER_LABEL,
    placeholder: MESSAGES.MAGIC_LINK.FORM.MANAGER_PLACEHOLDER,
    required: true,
    requiredMessage: MESSAGES.MAGIC_LINK.FORM.MANAGER_REQUIRED,
  },
};

// --- Component ---
interface MagicLinkModalProps {
  show: boolean;
  handleClose: () => void;
}

export const MagicLinkModal: React.FC<MagicLinkModalProps> = ({
  show,
  handleClose,
}) => {
  const [form] = Form.useForm<MagicLinkFormValues>();
  const { addToast } = useToast();

  const {
    data: managers,
    sendData: fetchManagers,
    isLoading: isLoadingManagers,
  } = usePost<ManagerItem[], null>();

  const { sendData: createMagicLink, isLoading: isCreating } = usePost<
    CreateMagicLinkResponse,
    CreateMagicLinkRequest
  >();

  useEffect(() => {
    if (show && !managers) {
      fetchManagers("GET_ALL_MANAGERS", null);
    }
  }, [show, managers, fetchManagers]);

  // Reset state when modal closes
  useEffect(() => {
    if (!show) {
      form.resetFields();
    }
  }, [show, form]);

  const managerOptions = useMemo(() => {
    if (!managers) return [];
    return managers.map((manager) => ({
      key: manager.manager_id,
      value: manager.manager_id,
      label: SelectableWithLogo(
        manager.manager_id,
        manager.manager_name,
        manager.image
      ),
    }));
  }, [managers]);

  const onFinish = async (values: MagicLinkFormValues): Promise<void> => {
    handleClose();
    const response = await createMagicLink("CREATE_MAGIC_LINK", {
      email: values.email,
      manager_id: values.manager_id,
    });
    addToast(
      response
        ? MESSAGES.MAGIC_LINK.CREATE_SUCCESS
        : MESSAGES.MAGIC_LINK.CREATE_ERROR,
      response ? "success" : "error"
    );
  };

  const footerContent = (
    <ModalFooter
      onClose={handleClose}
      onSubmit={(): void => form.submit()}
      closeText="Cancel"
      submitText={MESSAGES.MAGIC_LINK.CREATE_BUTTON}
      isLoading={isCreating}
      submitDisabled={isLoadingManagers}
      showDivider={false}
    />
  );

  return (
    <CompassModal
      show={show}
      handleClose={handleClose}
      size="lg"
      title={MESSAGES.MAGIC_LINK.DESCRIPTION.TITLE}
      titleIcon={linkIcon}
      maxHeight={450}
      footer={footerContent}
    >
      <ModalContent>
        <CompassParagraph color={greyText} lh={1.4} mb={16}>
          {MESSAGES.MAGIC_LINK.DESCRIPTION.TEXT}
        </CompassParagraph>
        <Form<MagicLinkFormValues>
          form={form}
          layout="vertical"
          size="large"
          requiredMark
          onFinish={onFinish}
        >
          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item
                name={FORM_FIELDS[FormFieldName.EMAIL].name}
                label={FORM_FIELDS[FormFieldName.EMAIL].label}
                rules={[
                  {
                    required: FORM_FIELDS[FormFieldName.EMAIL].required,
                    message: FORM_FIELDS[FormFieldName.EMAIL].requiredMessage,
                  },
                  {
                    type: "email",
                    message: MESSAGES.MAGIC_LINK.FORM.EMAIL_INVALID,
                  },
                ]}
              >
                <Input
                  placeholder={FORM_FIELDS[FormFieldName.EMAIL].placeholder}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                name={FORM_FIELDS[FormFieldName.MANAGER].name}
                label={FORM_FIELDS[FormFieldName.MANAGER].label}
                rules={[
                  {
                    required: FORM_FIELDS[FormFieldName.MANAGER].required,
                    message: FORM_FIELDS[FormFieldName.MANAGER].requiredMessage,
                  },
                ]}
              >
                <Select
                  placeholder={FORM_FIELDS[FormFieldName.MANAGER].placeholder}
                  loading={isLoadingManagers}
                  disabled={isLoadingManagers}
                  showSearch
                  optionLabelProp="label"
                  options={managerOptions}
                  dropdownStyle={{ zIndex: 2147483647 }}
                  getPopupContainer={(): HTMLElement => document.body}
                  filterOption={(input, option): boolean => {
                    const manager = managers?.find(
                      (m) => m.manager_id === option?.value
                    );
                    return manager
                      ? manager.manager_name
                          .toLowerCase()
                          .includes(input.toLowerCase())
                      : false;
                  }}
                  notFoundContent={
                    isLoadingManagers ? <Spin size="small" /> : null
                  }
                />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </ModalContent>
    </CompassModal>
  );
};
